import React, { useState, useContext, useEffect, useMemo } from "react";
import Dialog from "@mui/material/Dialog";
import { updateProductRecipe, addProductRecipe } from "api/product";
import { AppContext } from "Context/AppContext";
import AddEditRecipeOrderTable from "./AddEditRecipeOrderTable";
import { useFormikContext } from "formik";
import { getRowMaterialsList } from "api/inventoryProduct";
import { formatRawMaterialListData } from "../utility/util";
import { DEFAULT_STORE_ID } from "Helpers/Constants";
import OrderError from "Components/OrderError/OrderError";

function EditRecipeOrder(props) {
  const { showToastMessage, setIsLoading } = useContext(AppContext);
  const [recipe, setRecipe] = useState([]);
  const [rowMaterialList, setRowMaterialList] = useState([]);
  const [filteredRowMaterial, setFilteredRowMaterial] = useState([]);

  const formik = useFormikContext();

  const [deletedIngredientsIds, setDeletedIngredientsIds] = useState([]);

  // This function used to allow edit recipe or not
  const isOrderStatusEditable = () => {
    // If not edit order then allow to update recipe in any circumstances
    if (props.module !== "edit-order") {
      return true;
    }

    // For edit orders, check the order status
    const allowedStatuses = ["pending", "processing", "in_design"];
    return allowedStatuses.includes(props.orderStatus);
  };

  const filteredRawMaterials = (
    selectedProductRecipe = [],
    rowMaterialList = []
  ) => {
    const filteredData = rowMaterialList.filter(
      (latestRawMaterials) =>
        !selectedProductRecipe.some(
          (productRecipe) =>
            productRecipe.product_recipe_raw_materials_id ===
            latestRawMaterials.id
        )
    );
    setFilteredRowMaterial(filteredData || []);
  };

  //This function help us to identify is recipe has updated by CSR or not
  const isRecipeUpdate = (old_value = [], new_value = []) => {
    // Convert arrays to maps keyed by product_recipe_raw_materials_id
    const oldMap = new Map(
      old_value.map((item) => [item.product_recipe_raw_materials_id, item])
    );
    const newMap = new Map(
      new_value.map((item) => [item.product_recipe_raw_materials_id, item])
    );

    // If sizes differ, data has changed
    if (oldMap.size !== newMap.size) return true;

    for (const [id, oldItem] of oldMap.entries()) {
      const newItem = newMap.get(id);

      if (!newItem) return true; // Missing item

      // Keys to compare
      const keysToCompare = ["quantity", "product_recipe_raw_materials_id"];

      for (const key of keysToCompare) {
        if (oldItem[key] !== newItem[key]) {
          return true; // Found a change
        }
      }
    }

    return false; // Everything matches
  };

  //This function helps to format the product recipe ingredients
  //productType will have two enums "new_recipe" and "old_recipe"
  const formatOrderProductRecipe = (
    productType = "new_recipe",
    productRecipe = {}
  ) => {
    if (productRecipe && productRecipe?.product_recipe_raw_materials_id) {
      if (productType === "new_recipe") {
        return {
          quantity: productRecipe?.quantity || 0,
          product_recipe_raw_materials_id:
            productRecipe?.product_recipe_raw_materials_id || null,
          product_recipe_raw_materials_name:
            productRecipe?.product_recipe_raw_materials_name || "",
          product_recipe_types_id:
            productRecipe?.product_recipe_types_id || null,
          product_recipe_types_name:
            productRecipe?.product_recipe_types_name || "",
          product_recipe_units_id:
            productRecipe?.product_recipe_units_id || null,
          product_recipe_units_name:
            productRecipe?.product_recipe_units_name || "",
          product_recipe_raw_materials_whole_sale_price:
            productRecipe?.product_recipe_raw_materials_whole_sale_price || 0,
          product_recipe_raw_materials_retail_price:
            productRecipe?.product_recipe_raw_materials_retail_price || 0,
          raw_materials_price_id:
            productRecipe?.product_recipe_raw_material
              ?.product_recipe_raw_materials_prices[0]?.id || null,
        };
      } else {
        return {
          id: productRecipe?.id || null,
          quantity: productRecipe?.quantity || 0,
          product_recipe_raw_materials_id:
            productRecipe?.product_recipe_raw_materials_id || null,
          product_recipe_raw_materials_name:
            productRecipe?.product_recipe_raw_materials_name || "",
          product_recipe_types_id:
            productRecipe?.product_recipe_types_id || null,
          product_recipe_types_name:
            productRecipe?.product_recipe_types_name || "",
          product_recipe_units_id:
            productRecipe?.product_recipe_units_id || null,
          product_recipe_units_name:
            productRecipe?.product_recipe_units_name || "",
          product_recipe_raw_materials_whole_sale_price:
            productRecipe?.product_recipe_raw_materials_whole_sale_price || 0,
          product_recipe_raw_materials_retail_price:
            productRecipe?.product_recipe_raw_materials_retail_price || 0,
          raw_materials_price_id:
            productRecipe?.product_recipe_raw_material
              ?.product_recipe_raw_materials_prices[0]?.id || null,
        };
      }
    }
  };

  useEffect(() => {
    const recipeData = props?.editData?.data?.order_product_recipes || [];
    if (recipeData && recipeData.length > 0) {
      const formattedRecipeData = recipeData.map((value) => {
        return formatOrderProductRecipe(
          value?.id ? "old_recipe" : "new_recipe",
          value
        );
      });
      setRecipe(formattedRecipeData || []);
    }
  }, []);

  useEffect(() => {
    filteredRawMaterials(recipe || [], rowMaterialList || []);
  }, [rowMaterialList, recipe]);

  // Fetch raw materials data on component mount
  useEffect(() => {
    handleGetRowMaterial();
  }, []);

  //get row material data
  const handleGetRowMaterial = async () => {
    setIsLoading(true);
    try {
      const selectedStore =
        formik?.values?.billing_info?.zip_code_store_id ?? DEFAULT_STORE_ID;
      const response = await getRowMaterialsList(selectedStore);
      if (response && response.status === 200) {
        if (response.data?.data && response.data.data.length > 0) {
          const formattedData = formatRawMaterialListData(response.data.data);
          setRowMaterialList(formattedData);
          filteredRawMaterials(recipe || [], formattedData);
        }
      } else if (response && response.data?.message) {
        showToastMessage(undefined, response.data.message, "error", false);
      } else {
        showToastMessage(
          undefined,
          "Failed to fetch raw materials",
          "error",
          false
        );
      }
    } catch (error) {
      showToastMessage(
        undefined,
        "Failed to fetch raw materials",
        "error",
        false
      );
    }
    setIsLoading(false);
  };

  //Add new raw to add new recipe ingredients details
  const addNewRow = () => {
    const newIngredientsRow = {
      quantity: 0,
      product_recipe_raw_materials_id: "",
      product_recipe_raw_materials_name: "",
      product_recipe_types_id: null,
      product_recipe_types_name: "",
      product_recipe_units_id: null,
      product_recipe_units_name: "",
      product_recipe_raw_materials_whole_sale_price: 0,
      product_recipe_raw_materials_retail_price: 0,
      raw_materials_price_id: null,
    };
    const totalIngredients = [...recipe, newIngredientsRow];
    setRecipe(totalIngredients);
  };

  //Delete row of exiting ingredients or added ingredients
  const deleteRow = (value, existing_recipe_id = null) => {
    if (existing_recipe_id) {
      setDeletedIngredientsIds((prev) => [...prev, existing_recipe_id]);
    }
    setRecipe(recipe.filter((s) => s !== value));
  };

  //This function is handle to add and update the recipe Ingredients details
  const handleOnChange = (type, data, position = "") => {
    //Update the recipe Ingredients quantity
    if (type === "stockQuantity") {
      if (recipe[position]) {
        recipe[position]["quantity"] = data;
        let totalRecipeIngredients = [...recipe];
        setRecipe(totalRecipeIngredients);
      }
    }

    //Add recipe Ingredients details
    if (type === "stockRowMaterial" && data) {
      //Update the new recipe Ingredients details
      const selectedRawData = filteredRowMaterial.find((d) => d.id === data);
      if (!selectedRawData) {
        showToastMessage(
          undefined,
          "This ingredient is already selected",
          "error",
          false
        );
      }

      if (recipe && recipe.length > 0 && selectedRawData) {
        recipe[position]["product_recipe_raw_materials_id"] =
          selectedRawData?.id || null;
        recipe[position]["product_recipe_raw_materials_name"] =
          selectedRawData?.title || "";
        recipe[position]["product_recipe_types_id"] =
          selectedRawData?.product_recipe_type?.id || null;
        recipe[position]["product_recipe_types_name"] =
          selectedRawData?.product_recipe_type?.title || "";
        recipe[position]["product_recipe_units_id"] =
          selectedRawData?.product_recipe_unit?.id || null;
        recipe[position]["product_recipe_units_name"] =
          selectedRawData?.product_recipe_unit?.title || "";
        recipe[position]["product_recipe_raw_materials_whole_sale_price"] =
          selectedRawData?.whole_sale_price || 0;
        recipe[position]["product_recipe_raw_materials_retail_price"] =
          selectedRawData?.retail_price || 0;
        recipe[position]["raw_materials_price_id"] =
          selectedRawData?.product_recipe_raw_materials_prices[0]?.id || null;
        //Combine all recipe Ingredients data

        const filteredData = recipe.filter(
          (item, index) => item.product_recipe_raw_materials_id
        );
        setRecipe(filteredData);
      } else {
        const latestRecipeData = {
          quantity: 0,
          product_recipe_raw_materials_id: selectedRawData?.id || null,
          product_recipe_raw_materials_name: selectedRawData?.title || "",
          product_recipe_types_id:
            selectedRawData?.product_recipe_type?.id || null,
          product_recipe_types_name:
            selectedRawData?.product_recipe_type?.title || "",
          product_recipe_units_id:
            selectedRawData?.product_recipe_unit?.id || null,
          product_recipe_units_name:
            selectedRawData?.product_recipe_unit?.title || "",
          product_recipe_raw_materials_whole_sale_price:
            selectedRawData?.whole_sale_price || 0,
          product_recipe_raw_materials_retail_price:
            selectedRawData?.retail_price || 0,
          raw_materials_price_id:
            selectedRawData?.product_recipe_raw_materials_prices[0]?.id,
        };
        //Combine all recipe Ingredients data
        let totalRecipeIngredients = [...recipe, latestRecipeData];
        const filteredData = totalRecipeIngredients.filter(
          (item, index) => item.product_recipe_raw_materials_id
        );
        setRecipe(filteredData);
      }
    }
  };

  const handleSave = async () => {
    try {
      const originalRecipes =
        props?.editData?.data?.order_product_recipes || [];
      const selectedProduct = props?.editData?.data;
      // New validation: For each row, if either quantity or raw material is filled (but not both), show error
      const hasInvalidRow = recipe.some((item) => {
        const quantityFilled =
          item.quantity && item.quantity !== "" && item.quantity != 0;
        const rawMaterialFilled =
          item.product_recipe_raw_materials_name &&
          item.product_recipe_raw_materials_name !== "";

        // If either is filled but not both, it's invalid
        return (
          (quantityFilled || rawMaterialFilled) &&
          !(quantityFilled && rawMaterialFilled)
        );
      });

      if (hasInvalidRow) {
        showToastMessage(
          undefined,
          "Please add all ingredients data",
          "error",
          false
        );
        return; // Do not close the modal
      }

      // Check if there are any valid recipe items
      const validRecipe = recipe.filter(
        (item) =>
          item.quantity &&
          item.product_recipe_raw_materials_id &&
          item.product_recipe_raw_materials_name &&
          item.product_recipe_types_id &&
          item.product_recipe_types_name &&
          item.product_recipe_units_id &&
          item.product_recipe_units_name &&
          item.product_recipe_raw_materials_whole_sale_price &&
          item.product_recipe_raw_materials_retail_price
      );

      //There are not recipe ingredients are select then set empty order_product_recipe
      if (
        validRecipe.length === 0 &&
        originalRecipes.length === 0 &&
        !selectedProduct.sql_id
      ) {
        props.onSaveRecipe([]);
        showToastMessage(
          undefined,
          "Recipe updated successfully",
          "success",
          false
        );
        props.onClose();
        return;
      }

      if (props.module === "edit-order") {
        //If selected product already exiting for this order or not
        //If yes call realtime APIs
        if (selectedProduct?.sql_id && selectedProduct?.sql_id > 0) {
          //Check if recipe for selected product are exist or not.
          //If at least one is recipe is not exist then call add recipe API other wise update recipe API
          if (originalRecipes && originalRecipes.length === 0) {
            //Recipe not exist API
            const payload = {
              order_id: props.orderDetail.id,
              order_products_id: selectedProduct.sql_id,
              order_product_recipe_ingredients: recipe,
            };
            if (recipe && recipe.length > 0) {
              //Call API to add new recipe for exiting order product
              const response = await addProductRecipe(payload);

              if (response && response.data.data) {
                props.onSaveRecipe(
                  response.data.data || [],
                  selectedProduct.sql_id
                );
                props.onClose();
                showToastMessage(
                  undefined,
                  "Recipe updated successfully",
                  "success",
                  false
                );
              } else throw new Error("Something went Wrong");
            } else {
              props.onClose();
              showToastMessage(
                undefined,
                "Recipe updated successfully",
                "success",
                false
              );
            }
          } else {
            const isRecipeUpdated = isRecipeUpdate(originalRecipes, recipe);
            if (isRecipeUpdated) {
              const payload = {
                order_id: props.orderDetail.id,
                order_products_id: selectedProduct.sql_id,
                order_product_recipe_ingredients: recipe,
                deleted_ingredients_recipe_Ids: deletedIngredientsIds,
              };
              const response = await updateProductRecipe(payload);
              if (response && response.data.data) {
                props.onSaveRecipe(
                  response.data.data || [],
                  selectedProduct.sql_id
                );
                props.onClose();
                showToastMessage(
                  undefined,
                  "Recipe updated successfully",
                  "success",
                  false
                );
              } else throw new Error("Something went Wrong");
            } else {
              props.onSaveRecipe(recipe || [], null);
              props.onClose();
              showToastMessage(
                undefined,
                "Recipe updated successfully",
                "success",
                false
              );
            }
          }
        } else {
          //If selected product not exist then save it into the context
          props.onSaveRecipe(recipe || [], null);
          props.onClose();
          showToastMessage(
            undefined,
            "Recipe updated successfully",
            "success",
            false
          );
        }
      } else {
        props.onSaveRecipe(recipe || [], null);
        props.onClose();
        showToastMessage(
          undefined,
          "Recipe updated successfully",
          "success",
          false
        );
      }
    } catch (error) {
      showToastMessage(undefined, "Failed to save recipe", "error", false);
    }
  };

  // Calculate total recipe price
  const calculateTotalPrice = () => {
    return recipe.reduce((sum, item) => {
      const quantity = parseFloat(item.quantity) || 0;
      const price =
        parseFloat(item.product_recipe_raw_materials_retail_price) || 0;
      return sum + quantity * price;
    }, 0);
  };

  const totalPrice = calculateTotalPrice();


  //Check order details for order assigned to designer or not
  const isOrderAssignedToDesigner = useMemo(() => {
    if(props.module == "edit-order" &&  props.orderDetail && props.orderDetail.designer_id && 
        props.orderDetail.designer_id > 0 && props.orderDetail.order_status === "in_design"){
      return [
        {
          error: `This order is already "in design" with a designer. They may not see any changes made.`,
        },
      ];
    }
    return [];
  }, [props.orderDetail]);

  return (
    <Dialog
      open={props.open}
      onClose={props.onClose}
      maxWidth="md"
      fullWidth
      style={{ overflow: "hidden" }}
    >
      <div style={{ padding: "2rem", overflowX: "hidden" }}>
        <div className="">
          <div className="d-flex justify-content-start flex-column mb-3">
            <span style={{ fontSize: "22px", fontWeight: "bold" }}>
              {props.editData.data.title || "-"}
            </span>
            <span>{props.editData.data.selectedVariant.title || "-"}</span>
          </div>
            {isOrderAssignedToDesigner && isOrderAssignedToDesigner.length > 0 && (
              <OrderError errors={isOrderAssignedToDesigner} />
            )}

          <table className="rt-table table border-bottom">
            <thead className="rt-thead -header">
              <tr className="rt-tr">
                <th className="rt-th rt-resizable-header">Raw Material</th>
                <th className="rt-th rt-resizable-header">Unit</th>
                <th className="rt-th rt-resizable-header">Quantity</th>
                <th className="rt-th rt-resizable-header">Price</th>
                {isOrderStatusEditable() && (
                  <th className="rt-th rt-resizable-header">Action</th>
                )}
              </tr>
            </thead>
            <tbody className="rt-tbody">
              <AddEditRecipeOrderTable
                add={addNewRow}
                orderStatus={props.orderStatus}
                delete={deleteRow.bind()}
                recipe={recipe}
                onChange={handleOnChange}
                submitStatus={false}
                rowMaterialList={rowMaterialList}
                isOrderStatusEditable={isOrderStatusEditable()}
              />
              {/* Total Price Row */}
              <tr>
                <td
                  colSpan={3}
                  style={{
                    textAlign: "right",
                    fontWeight: "bold",
                    background: "#f8f9fa",
                  }}
                >
                  Total Price:
                </td>
                <td
                  style={{
                    textAlign: "left",
                    fontWeight: "bold",
                    background: "#f8f9fa",
                  }}
                >
                  ${totalPrice.toFixed(2)}
                </td>
                {isOrderStatusEditable() && (
                  <td style={{ background: "#f8f9fa" }}></td>
                )}
              </tr>
            </tbody>
          </table>
          <div className="text-center mt-3">
            {isOrderStatusEditable() && (
              <>
                
                <button
                  className="btn pink-btn d-inline-block mb-3 mr-2"
                  onClick={handleSave}
                >
                  Save
                </button>
                <button
                  className="btn gray-btn d-inline-block mb-3 mr-2"
                  onClick={props.onClose}
                >
                  Cancel
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </Dialog>
  );
}

export default EditRecipeOrder;
